
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

let wirkungPinMatchMedia = null;

const WIRKUNG_DESKTOP_QUERY = "(min-width: 992px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)";
const WIRKUNG_SCROLL_CARD_FACTOR = 1.75;
const WIRKUNG_SCROLL_MIN_CARD = 1250;
const WIRKUNG_SCROLL_TAIL_FACTOR = 0.42;

function getViewportHeight() {
  return Math.round(
    window.visualViewport?.height ||
    window.innerHeight ||
    document.documentElement.clientHeight ||
    0
  );
}

function getPinTotal(cards) {
  const h = getViewportHeight();
  const perCard = Math.max(WIRKUNG_SCROLL_MIN_CARD, h * WIRKUNG_SCROLL_CARD_FACTOR);
  return Math.round(perCard * cards.length + h * WIRKUNG_SCROLL_TAIL_FACTOR);
}

function getScrollTarget(st, cards, snapCenters, idx) {
  const start = Number.isFinite(st?.start) ? st.start : 0;
  return start + getPinTotal(cards) * (snapCenters[idx] ?? 0);
}

function scrollToTarget(target) {
  if (window.lenis && typeof window.lenis.scrollTo === "function") {
    window.lenis.scrollTo(target, { duration: 0.8, easing: (t) => 1 - Math.pow(1 - t, 3) });
    return;
  }

  window.scrollTo({ top: target, behavior: "smooth" });
}

function killSnapTween(st) {
  const tween = typeof st?.getTween === "function" ? st.getTween(true) : null;
  if (tween && typeof tween.kill === "function") tween.kill();
}

function setupWirkungPin(section) {
  const pin = section.querySelector(".wirkung-pin-group");
  const stage = section.querySelector(".wirkung-pin-stage");
  const cards = Array.from(section.querySelectorAll(".wirkung-cards-container > .wirkung-card-wrapper"));

  if (!pin || !stage || cards.length < 2) return null;

  section.querySelector(".wirkung-pin-nav")?.remove();
  section.classList.add("is-wirkung-pin");

  const snapCenters = cards.map((_, i) => (i + 0.5) / cards.length);
  const nav = document.createElement("nav");
  nav.className = "wirkung-pin-nav";
  nav.setAttribute("aria-label", "Web-Projekte ausw\u00e4hlen");
  pin.appendChild(nav);

  let st = null;
  let activeIdx = -1;

  const buttons = cards.map((card, idx) => {
    const label = String(idx + 1).padStart(2, "0");
    const button = document.createElement("button");
    const number = document.createElement("span");
    const dot = document.createElement("span");

    button.type = "button";
    button.className = "wirkung-pin-nav-btn";
    button.setAttribute("aria-label", `Web-Projekt ${label} anzeigen`);
    number.className = "wirkung-pin-nav-number";
    number.textContent = label;
    dot.className = "wirkung-pin-nav-dot";
    dot.setAttribute("aria-hidden", "true");

    button.append(number, dot);
    button.addEventListener("click", () => {
      if (!st) return;
      killSnapTween(st);
      scrollToTarget(getScrollTarget(st, cards, snapCenters, idx));
    });

    nav.appendChild(button);
    card.style.zIndex = String(100 + idx);
    return button;
  });

  const setActive = (idx) => {
    const nextIdx = Math.max(0, Math.min(cards.length - 1, idx));
    if (nextIdx === activeIdx) return;
    activeIdx = nextIdx;

    cards.forEach((card, i) => {
      const active = i === activeIdx;
      card.classList.toggle("is-active", active);
      card.setAttribute("aria-hidden", active ? "false" : "true");
      if ("inert" in card) card.inert = !active;
    });

    buttons.forEach((button, i) => {
      const active = i === activeIdx;
      button.classList.toggle("is-active", active);
      if (active) {
        button.setAttribute("aria-current", "true");
      } else {
        button.removeAttribute("aria-current");
      }
    });
  };

  const getIndexFromProgress = (progress) => {
    const slot = Math.min(0.999999, Math.max(0, progress)) * cards.length;
    return Math.max(0, Math.min(cards.length - 1, Math.floor(slot)));
  };

  setActive(0);

  st = ScrollTrigger.create({
    trigger: section,
    start: "top top",
    end: () => `+=${getPinTotal(cards)}`,
    pin,
    pinSpacing: true,
    anticipatePin: 1,
    scrub: true,
    invalidateOnRefresh: true,
    snap: {
      snapTo: snapCenters,
      duration: { min: 0.12, max: 0.24 },
      delay: 0,
      ease: "power1.out",
      inertia: false,
      directional: false
    },
    onUpdate: (self) => {
      setActive(getIndexFromProgress(self.progress));
    },
    onLeave: () => setActive(cards.length - 1),
    onLeaveBack: () => setActive(0),
    onRefresh: (self) => {
      setActive(getIndexFromProgress(self.progress));
    }
  });

  return () => {
    if (st) {
      st.kill();
      st = null;
    }

    section.classList.remove("is-wirkung-pin");
    nav.remove();
    activeIdx = -1;

    cards.forEach((card) => {
      card.classList.remove("is-active");
      card.removeAttribute("aria-hidden");
      if ("inert" in card) card.inert = false;
      card.style.zIndex = "";
    });
  };
}

export function initArbeitSection() {
  const sections = Array.from(document.querySelectorAll(".section-wirkung[data-wirkung-pin]"));
  if (!sections.length || wirkungPinMatchMedia) return;

  wirkungPinMatchMedia = gsap.matchMedia();
  wirkungPinMatchMedia.add(WIRKUNG_DESKTOP_QUERY, () => {
    const cleanups = sections.map(setupWirkungPin).filter(Boolean);

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  });
}
